import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Minus, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setSize(r.data.sizes?.[0] || null);
    }).catch(() => toast.error("Product not found"));
  }, [id]);

  if (!product) {
    return <div className="pt-40 text-center text-white/50" data-testid="product-loading">Loading…</div>;
  }

  const onAdd = () => {
    add(product, size, qty);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="product-detail-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/shop" className="text-sm uppercase tracking-widest text-white/60 hover:text-[var(--davide-gold)] inline-flex items-center gap-2 mb-10" data-testid="back-to-shop-link">
          <ArrowLeft size={14} /> Back to Shop
        </Link>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          <div className="relative">
            <img src={product.image_url} alt={product.name} className="w-full h-[70vh] md:h-[80vh] object-cover" data-testid="product-image" />
            <div className="absolute bottom-3 left-3 overline" style={{background:"rgba(10,10,10,0.75)", padding:"4px 10px"}}>{product.category}</div>
          </div>

          <div className="flex flex-col justify-center">
            <p className="overline mb-4">{product.category}</p>
            <h1 className="font-display text-4xl md:text-6xl font-light text-white leading-tight tracking-tight" data-testid="product-name">{product.name}</h1>
            <p className="font-display text-3xl text-[var(--davide-gold)] mt-4" data-testid="product-price">${product.price.toFixed(2)}</p>
            <p className="text-white/70 mt-8 leading-relaxed" data-testid="product-description">{product.description}</p>

            {product.sizes?.length > 0 && (
              <div className="mt-10">
                <div className="overline mb-3" style={{color:"var(--davide-muted)"}}>Size</div>
                <div className="flex flex-wrap gap-2" data-testid="size-selector">
                  {product.sizes.map((s) => (
                    <button key={s} onClick={() => setSize(s)}
                      data-testid={`size-${s}-btn`}
                      className={`px-5 py-2.5 text-xs uppercase tracking-widest transition-all ${
                        size === s
                          ? "bg-[var(--davide-gold)] text-black"
                          : "border border-white/20 text-white/70 hover:border-[var(--davide-gold)]"
                      }`}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <div className="overline mb-3" style={{color:"var(--davide-muted)"}}>Quantity</div>
              <div className="flex items-center gap-4">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="border border-white/20 w-10 h-10 flex items-center justify-center hover:border-[var(--davide-gold)]" data-testid="qty-decrease-btn"><Minus size={14}/></button>
                <span className="w-8 text-center text-white text-lg" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="border border-white/20 w-10 h-10 flex items-center justify-center hover:border-[var(--davide-gold)]" data-testid="qty-increase-btn"><Plus size={14}/></button>
              </div>
            </div>

            <button onClick={onAdd} className="btn-gold mt-10 justify-center" data-testid="add-to-cart-btn">
              Add to Cart — ${(product.price * qty).toFixed(2)}
            </button>
            {product.external_url && (
              <a href={product.external_url} target="_blank" rel="noopener noreferrer"
                data-testid="external-product-link"
                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--davide-gold)] hover:underline">
                <ExternalLink size={12} />
                {product.external_url.includes("aliveshoes") ? "View on AliveShoes" : "View on Rachvid Wears"}
              </a>
            )}
            <p className="text-xs text-white/40 mt-6">Free worldwide shipping on orders over $150. A portion of every sale supports YAAIDFI.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
